import React, {
  Dispatch,
  ReactNode,
  createContext,
  useEffect,
  useReducer,
  SetStateAction
} from 'react';
interface Role {
  item1?: number;
  item2?: string;
}
interface Token {
  accessToken: string;
  expiresIn?: number;
  refreshToken?: string;
  roles?: Role[] | [];
  impersonatedByUser?: string;
}

interface State {
  token: Token | null;
  loading: boolean;
  error: string | null;
}

type Action =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: Token }
  | { type: 'LOGIN_FAIL'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'IMPERSONATE_START'; payload: Token }
  | { type: 'END_IMPERSONATE'; payload: Token }
  | { type: 'SET_ACCESS_TOKEN'; payload: { accessToken: string } };

const INITIAL_STATE: State = {
  token: JSON.parse(localStorage.getItem('token') as string) || null,
  loading: false,
  error: null
};

interface AuthContextValue extends State {
  dispatch: Dispatch<Action>;
  setIsImpersonating: Dispatch<SetStateAction<boolean>>;
  isImpersonating: boolean;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
);

const AuthReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        token: null,
        loading: true,
        error: null
      };
    case 'LOGIN_SUCCESS':
      return {
        token: action.payload,
        loading: false,
        error: null
      };
    case 'LOGIN_FAIL':
      return {
        token: null,
        loading: false,
        error: action.payload
      };
    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('impersonateToken');
      return {
        token: null,
        loading: false,
        error: null
      };
    case 'IMPERSONATE_START': {
      const {
        accessToken,
        expiresIn,
        refreshToken,
        roles,
        impersonatedByUser
      } = action.payload;

      if (!state.token) {
        return state;
      }
      localStorage.setItem('impersonateToken', JSON.stringify(state.token)); // Save original token

      return {
        ...state,
        token: {
          accessToken,
          expiresIn,
          refreshToken,
          roles,
          impersonatedByUser // Save impersonation info
        }
      };
    }
    case 'END_IMPERSONATE': {
      if (!state.token) {
        return state;
      }
      localStorage.removeItem('impersonateToken');

      return {
        ...state,
        token: action.payload
      };
    }
    case 'SET_ACCESS_TOKEN':
      return {
        ...state,
        token: {
          ...state.token,
          accessToken: action.payload.accessToken
        }
      };
    default:
      return state;
  }
};

interface AuthContextProviderProps {
  children: ReactNode;
}

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({
  children
}) => {
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);

  useEffect(() => {
    if (state.token) {
      localStorage.setItem('token', JSON.stringify(state.token));
    }
  }, [state.token]);

  const [isImpersonating, setIsImpersonating] = React.useState(false);

  return (
    <AuthContext.Provider
      value={{ ...state, dispatch, isImpersonating, setIsImpersonating }}
    >
      {children}
    </AuthContext.Provider>
  );
};
